import { format, parseISO, subDays } from 'date-fns';
import type { PatchVendingProductBody } from './api';
import { TOURNAMENT_LOCALES } from './tournaments';
import type {
  AdminVendingProduct,
  VendingLocale,
  VendingProductDraft,
  VendingTranslation,
} from './types';

/** Integer tetri to the staff-facing GEL decimal representation. */
export function formatTetri(minor: number): string {
  return (minor / 100).toFixed(2);
}

/** Parse a non-negative GEL decimal with no more than two fractional places. */
export function gelToTetri(value: string): number | null {
  const normalized = value.trim();
  const match = /^(\d+)(?:\.(\d{1,2}))?$/.exec(normalized);
  if (!match) return null;
  const whole = Number(match[1]);
  const fraction = Number((match[2] ?? '').padEnd(2, '0'));
  const tetri = whole * 100 + fraction;
  return Number.isSafeInteger(tetri) ? tetri : null;
}

/** Admin display fallback is English, then Georgian, then Russian. */
export function productDisplayName(
  translations: Partial<Record<VendingLocale, VendingTranslation>>,
): string {
  return translations.en?.name || translations.ka?.name || translations.ru?.name || '—';
}

/**
 * Return precisely the fields the API needs for an edit, or null when nothing
 * changed. A locale explicitly cleared in the controlled form is represented
 * as `null`, which asks the API to remove its translation row.
 */
export function diffProduct(
  original: AdminVendingProduct,
  draft: VendingProductDraft,
): PatchVendingProductBody | null {
  const patch: PatchVendingProductBody = {};
  if (draft.image_url !== undefined && (draft.image_url ?? null) !== (original.image_url ?? null)) {
    patch.image_url = draft.image_url;
  }
  if (draft.price_minor !== original.price_minor) patch.price_minor = draft.price_minor;
  if (draft.display_order !== original.display_order) patch.display_order = draft.display_order;
  if (draft.is_active !== original.is_active) patch.is_active = draft.is_active;

  const translations: Record<string, { name: string; description: string | null } | null> = {};
  for (const locale of TOURNAMENT_LOCALES) {
    const existing = original.translations[locale];
    const edited = draft.translations[locale];
    // A missing draft key is not an instruction to erase an existing locale.
    if (!edited) continue;
    const name = edited.name.trim();
    const description = edited.description.trim() || null;

    if (!existing) {
      if (name) translations[locale] = { name, description };
      continue;
    }
    if (!name && description === null) {
      translations[locale] = null;
      continue;
    }
    if (name !== existing.name || description !== existing.description) {
      translations[locale] = { name, description };
    }
  }
  if (Object.keys(translations).length > 0) patch.translations = translations;

  return Object.keys(patch).length > 0 ? patch : null;
}

/** Inclusive rolling thirty-day range in venue-date (YYYY-MM-DD) form. */
export function defaultRange(today: string): { from: string; to: string } {
  return { from: format(subDays(parseISO(today), 29), 'yyyy-MM-dd'), to: today };
}

/** `null` means valid; otherwise the returned sentence is suitable inline help. */
export function validateSlug(id: string): string | null {
  return /^[a-z0-9_]{2,32}$/.test(id)
    ? null
    : 'Id must use 2–32 lowercase letters, numbers, or underscores.';
}
